/**
 * Survey data store.
 * Manages survey questions, logic rules, and settings.
 */

import { create } from 'zustand';
import type {
  Survey,
  SurveySettings,
  SurveyTheme,
  SurveyPage,
  Question,
  LogicRule,
} from '@/types';
import { getSurveyData } from '@/lib/api';

interface SurveyState {
  // Survey data
  surveyId: string | null;
  survey: Survey | null;
  questions: Question[];
  logicRules: LogicRule[];
  pages: SurveyPage[];
  versionId: string | null;

  // Locale (Phase 78). `isRtl` drives the layout text direction.
  resolvedLocale: string | null;
  isRtl: boolean;

  // Settings
  settings: SurveySettings;
  theme: SurveyTheme;

  // Loading state
  isLoading: boolean;
  error: string | null;

  // Actions
  loadSurvey: (
    surveyId: string,
    sessionId: string,
    locale?: string,
  ) => Promise<void>;
  setSurveyData: (data: {
    survey: Survey;
    questions: Question[];
    logic: LogicRule[];
    pages: SurveyPage[];
    versionId: string;
  }) => void;
  getQuestionByCode: (code: string) => Question | undefined;
  getQuestionsByPage: (page: number) => Question[];
  getVisibleQuestions: (page: number, hiddenCodes: Set<string>) => Question[];
  reset: () => void;
}

const DEFAULT_SETTINGS: SurveySettings = {
  allowBackButton: true,
  showProgressBar: true,
  progressBarType: 'percentage',
  autoSave: true,
  autoSaveInterval: 30,
  sessionTimeout: 30,
};

const DEFAULT_THEME: SurveyTheme = {};

export const useSurveyStore = create<SurveyState>((set, get) => ({
  // Initial state
  surveyId: null,
  survey: null,
  questions: [],
  logicRules: [],
  pages: [],
  versionId: null,
  resolvedLocale: null,
  isRtl: false,
  settings: DEFAULT_SETTINGS,
  theme: DEFAULT_THEME,
  isLoading: false,
  error: null,

  // Load survey data from API
  loadSurvey: async (surveyId: string, sessionId: string, locale?: string) => {
    set({ isLoading: true, error: null });

    try {
      const data = await getSurveyData(surveyId, sessionId, locale);

      set({
        surveyId,
        survey: data.survey,
        questions: data.questions,
        logicRules: data.logic,
        pages: data.pages,
        versionId: data.versionId,
        resolvedLocale: data.resolvedLocale ?? null,
        isRtl: data.isRtl ?? false,
        settings: { ...DEFAULT_SETTINGS, ...data.survey.settings },
        theme: { ...DEFAULT_THEME, ...data.survey.theme },
        isLoading: false,
      });
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error ? error.message : 'Failed to load survey',
      });
      throw error;
    }
  },

  // Set survey data directly (from session start response)
  setSurveyData: (data) => {
    set({
      surveyId: data.survey.id,
      survey: data.survey,
      questions: data.questions,
      logicRules: data.logic,
      pages: data.pages,
      versionId: data.versionId,
      settings: { ...DEFAULT_SETTINGS, ...data.survey.settings },
      theme: { ...DEFAULT_THEME, ...data.survey.theme },
    });
  },

  // Get question by code
  getQuestionByCode: (code: string) => {
    const { questions } = get();
    return questions.find((q) => q.code === code);
  },

  // Get all questions for a page
  getQuestionsByPage: (page: number) => {
    const { questions } = get();
    return questions
      .filter((q) => q.page === page)
      .sort((a, b) => a.order - b.order);
  },

  // Get visible questions for a page (excluding hidden)
  getVisibleQuestions: (page: number, hiddenCodes: Set<string>) => {
    const { questions } = get();
    return questions
      .filter((q) => q.page === page && !hiddenCodes.has(q.code))
      .sort((a, b) => a.order - b.order);
  },

  // Reset store
  reset: () => {
    set({
      surveyId: null,
      survey: null,
      questions: [],
      logicRules: [],
      pages: [],
      versionId: null,
      resolvedLocale: null,
      isRtl: false,
      settings: DEFAULT_SETTINGS,
      theme: DEFAULT_THEME,
      isLoading: false,
      error: null,
    });
  },
}));
